import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from './Header';
import Footer from './Footer';

const OTPVerification = ({ onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, error, user } = useAuth();
  
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendError, setResendError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Get email from location state or redirect to onboarding
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      navigate('/onboarding');
    }
  }, [location.state, navigate]);

  // Note: For MVP, we show success screen and stop here
  // Profile completion will be added in future versions

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  const handleOtpChange = (e) => {
    const value = e.target.value;
    // Only allow digits, max 6 characters
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
      if (errors.otp) {
        setErrors(prev => ({ ...prev, otp: '' }));
      }
    }
  };

  const validateOtp = () => {
    const newErrors = {};

    if (!otp) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateOtp()) return;

    setVerifyLoading(true);

    try {
      await verifyOTP(email, otp);
      // OTP verified successfully, show success screen
      setShowSuccess(true);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'OTP verification failed';
      setErrors(prev => ({ ...prev, otp: errorMsg }));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendError('');
    setResendLoading(true);

    try {
      // For now, we'll use a POST to resend-otp endpoint
      const response = await fetch('https://api-manager-b7jc.onrender.com/api/v1/auth/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend OTP');
      }

      // Start 2-minute (120 second) countdown
      setResendTimer(120);
      setResendError('');
    } catch (err) {
      setResendError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToHome = () => {
    // Clear authentication state
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="bg-offwhite text-charcoal antialiased">
      <Header onNavigateToOnboarding={onBack} isOnboarding={true} />
      
      <div className="min-h-screen py-32 px-6 bg-gradient-to-b from-offwhite to-blush">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {showSuccess ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-6">🎉</div>
                <h2 className="text-2xl font-playfair font-bold text-charcoal mb-4">You're All Set!</h2>
                <p className="text-charcoal/70 mb-8 max-w-md mx-auto">
                  Welcome, {user?.firstName}! 👋<br/>
                  You're on the early-access list.
                </p>
                <div className="bg-gradient-to-r from-rose/10 to-transparent border-2 border-rose/20 rounded-xl p-6 max-w-md mx-auto mb-8">
                  <p className="text-charcoal/80">
                    <strong className="text-rose">What's Next?</strong><br/>
                    We'll notify you once your profile is activated
                  </p>
                  <br/>
                  <p>
                    Status: <strong className="text-rose">Pending</strong><br/>
                  </p>
                </div>
                {/* <div className="flex justify-center mb-6">
                  <a 
                    href="https://facebook.com/wako" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    title="Follow us on Facebook"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div> */}
                <button
                  onClick={handleBackToHome}
                  className="px-8 py-3 bg-rose text-white rounded-md font-semibold hover:bg-charcoal transition-colors duration-300"
                >
                  Back to Home
                </button>
              </div>
            ) : (
              <form onSubmit={handleVerifyOtp}>
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-playfair font-bold text-charcoal mb-3">Verify Your Email</h2>
                    <p className="text-charcoal/70">We sent a verification code to<br/><strong>{email}</strong></p>
                  </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Verification Code *</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={handleOtpChange}
                    placeholder="000000"
                    disabled={verifyLoading}
                    maxLength="6"
                    className={`w-full px-3 py-2 rounded-xl border-2 text-center text-xl tracking-widest font-mono ${
                      errors.otp ? 'border-rose' : 'border-charcoal/20'
                    } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
                  />
                  {/* <p className="text-xs text-charcoal/50 mt-2">Enter the 6-digit code sent to your email</p> */}
                </div>

                <div className="bg-blush/30 rounded-xl p-3 text-xs text-charcoal/70">
                  <p>💡 Check your email (including spam folder) for the verification code</p>
                </div>

                {errors.otp && (
                  <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
                    {errors.otp}
                  </div>
                )}

                {error && !errors.otp && (
                  <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
                    {error}
                  </div>
                )}

                {resendError && (
                  <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
                    {resendError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={verifyLoading}
                  className="w-full bg-rose text-white py-3 rounded-xl font-semibold hover:bg-charcoal transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifyLoading ? 'Verifying...' : 'Verify Email'}
                </button>

                <div className="text-center">
                  <p className="text-xs text-charcoal/60">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || resendLoading || verifyLoading}
                      className={`font-semibold transition-colors ${
                        resendTimer > 0
                          ? 'text-charcoal/50 cursor-not-allowed'
                          : 'text-rose hover:text-charcoal'
                      }`}
                    >
                      {resendLoading
                        ? 'Sending...'
                        : resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : 'Resend OTP'}
                    </button>
                  </p>
                </div>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default OTPVerification;
